import React, { useEffect, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';
import './VideoSettings.css';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { Block } from '../classes/Block';
import type { CardGroup } from '../utils/cardGroups';
import type { DefaultCardSettings } from '../utils/defaultCardSettings';

type DefaultCardSettingField = keyof DefaultCardSettings;

interface CardOverrideDialogProps {
  open: boolean;
  blocks: Block[];
  cardGroups: CardGroup[];
  changedField: DefaultCardSettingField | null;
  changedValue: number | null;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onApply: () => void;
  onSkip: () => void;
}

const CardOverrideDialog: React.FC<CardOverrideDialogProps> = ({
  open,
  blocks,
  cardGroups,
  changedField,
  changedValue,
  selectedIds,
  onToggle,
  onSelectAll,
  onApply,
  onSkip,
}) => {
  const blockById: Map<string, Block> = new Map(blocks.map((b) => [b.id, b]));

  const getSettingLabel = (field: DefaultCardSettingField | null): string => {
    if (field === 'cardFadeInFrames') return String(t`Fade In`);
    if (field === 'cardDurationFullyUpFrames') return String(t`Duration fully up`);
    if (field === 'cardFadeOutFrames') return String(t`Fade Out`);
    if (field === 'cardSlugFrames') return String(t`Slug`);
    return '';
  };

  const getBlockFieldValue = (block: Block, field: DefaultCardSettingField): number => {
    if (field === 'cardFadeInFrames') return block.cardFadeInFrames ?? 0;
    if (field === 'cardDurationFullyUpFrames') return block.cardDurationFullyUpFrames ?? 0;
    if (field === 'cardFadeOutFrames') return block.cardFadeOutFrames ?? 0;
    return block.cardSlugFrames ?? 0;
  };

  const getCurrentValueLabel = (group: CardGroup): string => {
    if (!changedField) return '';
    const groupBlocks: Block[] = group.blockIds
      .map((id: string) => blockById.get(id))
      .filter((block: Block | undefined): block is Block => block !== undefined);

    if (groupBlocks.length === 0) {
      return `0 ${String(t`frames`)}`;
    }

    const firstValue: number = getBlockFieldValue(groupBlocks[0], changedField);
    const hasMixedValues: boolean = groupBlocks.some(
      (block: Block) => getBlockFieldValue(block, changedField) !== firstValue
    );

    if (hasMixedValues) {
      return String(t`mixed`);
    }

    return `${firstValue} ${String(t`frames`)}`;
  };

  const cardOptions = cardGroups.map((group) => {
    const blockNames: string = group.blockIds
      .map((id: string) => blockById.get(id)?.name)
      .filter((name: string | undefined): name is string => Boolean(name))
      .join(', ');
    const currentValueLabel: string = getCurrentValueLabel(group);
    const label: string = blockNames
      ? `${group.name} (${blockNames}) - ${String(t`current`)}: ${currentValueLabel}`
      : `${group.name} - ${String(t`current`)}: ${currentValueLabel}`;
    return { id: group.id, label };
  });

  const allSelected = selectedIds.size === cardGroups.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  return (
    <Dialog open={open} onClose={onSkip} maxWidth="sm" fullWidth>
      <DialogTitle>{t`Apply to Existing Cards`}</DialogTitle>
      <DialogContent className="default-card-override-dialog">
        {changedField !== null && changedValue !== null ? (
          <p className="default-card-override-dialog-lead">
            {`${String(t`Setting`)}: ${getSettingLabel(changedField)} -> ${changedValue} ${String(t`frames`)}`}
          </p>
        ) : null}
        <p className="default-card-override-dialog-hint">
          {t`Select the cards you want to override with the new default value:`}
        </p>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={onSelectAll}
                color="primary"
              />
            }
            label={<strong>{allSelected ? t`Unselect All` : t`Select All`}</strong>}
          />
          <div className="default-card-override-dialog-nested">
            {cardOptions.map((option) => (
              <FormControlLabel
                key={option.id}
                control={
                  <Checkbox
                    checked={selectedIds.has(option.id)}
                    onChange={() => onToggle(option.id)}
                    color="primary"
                  />
                }
                label={option.label}
              />
            ))}
          </div>
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSkip} color="inherit">
          {t`Skip`}
        </Button>
        <Button onClick={onApply} variant="contained" disableElevation>
          {t`Apply`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export interface DefaultCardSettingsPanelProps {
  defaultCardSettings: DefaultCardSettings;
  onDefaultsChange: (
    newDefaults: DefaultCardSettings,
    changedField: DefaultCardSettingField,
    overrideCardGroupIds: string[]
  ) => void;
  blocks: Block[];
  cardGroups: CardGroup[];
  fps: number;
}

export const DefaultCardSettingsPanel: React.FC<DefaultCardSettingsPanelProps> = ({
  defaultCardSettings,
  onDefaultsChange,
  blocks,
  cardGroups,
  fps,
}) => {
  const [fadeInInput, setFadeInInput] = useState<string>(String(defaultCardSettings.cardFadeInFrames));
  const [durationInput, setDurationInput] = useState<string>(String(defaultCardSettings.cardDurationFullyUpFrames));
  const [fadeOutInput, setFadeOutInput] = useState<string>(String(defaultCardSettings.cardFadeOutFrames));
  const [slugInput, setSlugInput] = useState<string>(String(defaultCardSettings.cardSlugFrames));

  const [pendingDefaults, setPendingDefaults] = useState<DefaultCardSettings | null>(null);
  const [pendingField, setPendingField] = useState<DefaultCardSettingField | null>(null);
  const [pendingValue, setPendingValue] = useState<number | null>(null);
  const [overrideSelectedIds, setOverrideSelectedIds] = useState<Set<string>>(new Set());

  const isUpdatingFromProps = useRef<boolean>(false);

  useEffect(() => {
    if (isUpdatingFromProps.current) return;
    setFadeInInput(String(defaultCardSettings.cardFadeInFrames));
    setDurationInput(String(defaultCardSettings.cardDurationFullyUpFrames));
    setFadeOutInput(String(defaultCardSettings.cardFadeOutFrames));
    setSlugInput(String(defaultCardSettings.cardSlugFrames));
  }, [defaultCardSettings]);

  const parseFrame = (raw: string): number => {
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };

  const openDialog = (
    newDefaults: DefaultCardSettings,
    changedField: DefaultCardSettingField,
    changedValue: number
  ) => {
    isUpdatingFromProps.current = true;
    if (cardGroups.length > 0) {
      setOverrideSelectedIds(new Set(cardGroups.map((g) => g.id)));
      setPendingDefaults(newDefaults);
      setPendingField(changedField);
      setPendingValue(changedValue);
    } else {
      onDefaultsChange(newDefaults, changedField, []);
      isUpdatingFromProps.current = false;
    }
  };

  const handleDialogApply = () => {
    if (!pendingDefaults || !pendingField) return;
    isUpdatingFromProps.current = false;
    onDefaultsChange(pendingDefaults, pendingField, Array.from(overrideSelectedIds));
    setPendingDefaults(null);
    setPendingField(null);
    setPendingValue(null);
  };

  const handleDialogSkip = () => {
    if (!pendingDefaults || !pendingField) return;
    isUpdatingFromProps.current = false;
    onDefaultsChange(pendingDefaults, pendingField, []);
    setPendingDefaults(null);
    setPendingField(null);
    setPendingValue(null);
  };

  const handleToggle = (id: string) => {
    setOverrideSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (overrideSelectedIds.size === cardGroups.length) {
      setOverrideSelectedIds(new Set());
    } else {
      setOverrideSelectedIds(new Set(cardGroups.map((g) => g.id)));
    }
  };

  const commitFrameField = (
    field: DefaultCardSettingField,
    rawValue: string
  ) => {
    const value: number = parseFrame(rawValue);
    if (value === defaultCardSettings[field]) return;
    openDialog({ ...defaultCardSettings, [field]: value }, field, value);
  };

  const totalFrames =
    defaultCardSettings.cardFadeInFrames +
    defaultCardSettings.cardDurationFullyUpFrames +
    defaultCardSettings.cardFadeOutFrames +
    defaultCardSettings.cardSlugFrames;
  const totalSeconds = fps > 0 ? totalFrames / fps : 0;
  const totalLabel =
    fps > 0
      ? `${totalFrames} ${t`frames`} (${totalSeconds.toFixed(2)} ${t`seconds`})`
      : `${totalFrames} ${t`frames`}`;

  return (
    <div className="video-settings-panel">
      <div className="video-settings-group">
        <div className="setting-item">
          <label>{t`Fade In`}</label>
          <div className="input-wrapper">
            <div className="video-time-controls">
              <input
                type="number"
                min="0"
                step="1"
                value={fadeInInput}
                onChange={(e) => setFadeInInput(e.target.value)}
                onBlur={() => commitFrameField('cardFadeInFrames', fadeInInput)}
                className="video-time-input"
              />
              <span className="video-settings-addon">{t`frames`}</span>
            </div>
          </div>
        </div>

        <div className="setting-item">
          <label>{t`Duration fully up`}</label>
          <div className="input-wrapper">
            <div className="video-time-controls">
              <input
                type="number"
                min="0"
                step="1"
                value={durationInput}
                onChange={(e) => setDurationInput(e.target.value)}
                onBlur={() => commitFrameField('cardDurationFullyUpFrames', durationInput)}
                className="video-time-input"
              />
              <span className="video-settings-addon">{t`frames`}</span>
            </div>
          </div>
        </div>

        <div className="setting-item">
          <label>{t`Fade Out`}</label>
          <div className="input-wrapper">
            <div className="video-time-controls">
              <input
                type="number"
                min="0"
                step="1"
                value={fadeOutInput}
                onChange={(e) => setFadeOutInput(e.target.value)}
                onBlur={() => commitFrameField('cardFadeOutFrames', fadeOutInput)}
                className="video-time-input"
              />
              <span className="video-settings-addon">{t`frames`}</span>
            </div>
          </div>
        </div>

        <div className="setting-item">
          <label>{t`Slug`}</label>
          <div className="input-wrapper">
            <div className="video-time-controls">
              <input
                type="number"
                min="0"
                step="1"
                value={slugInput}
                onChange={(e) => setSlugInput(e.target.value)}
                onBlur={() => commitFrameField('cardSlugFrames', slugInput)}
                className="video-time-input"
              />
              <span className="video-settings-addon">{t`frames`}</span>
            </div>
          </div>
        </div>

        <div className="setting-item">
          <label>{t`Full time`}</label>
          <div className="input-wrapper">
            <span className="video-settings-value-text">{totalLabel}</span>
          </div>
        </div>

      </div>

      <CardOverrideDialog
        open={pendingDefaults !== null}
        blocks={blocks}
        cardGroups={cardGroups}
        changedField={pendingField}
        changedValue={pendingValue}
        selectedIds={overrideSelectedIds}
        onToggle={handleToggle}
        onSelectAll={handleSelectAll}
        onApply={handleDialogApply}
        onSkip={handleDialogSkip}
      />
    </div>
  );
};

export default DefaultCardSettingsPanel;
